import { lazy, Suspense, useContext, useState, useEffect, useRef } from '@wordpress/element';
import LoadingSpinner from '../LoadingSpinner';
import OrderType from './OrderType';
import { formattedPrice, totalItemsInCart } from './util';
import CartProducts from './CartProducts';
import { ConfigContext } from '../../index.js';
import AddressInput from './AddressInput';
import { ToggleSidebarContext, RetailerIdContext } from '../../App.jsx';
import {
    useRetailerData,
    useCartData,
    useSpecialsList,
} from '../../hooks/dutchie-queries.js';
import { gtmTrackEvent, processCartSpecials } from '../../analytics';
import { useSharedOrderType } from '../../hooks/useSharedOrderType';
import useFetchRest from '../../hooks/useFetchRest';
import { didSurfsideLoad, trackSurfsideProductsLoop } from '../Surfside/util';
const Surfside = lazy(() => import('../Surfside/Surfside'));


const CartSidebar = ({ forcedOrderType }) => {
    const { sidebarToggle, setSidebarToggle } =
        useContext(ToggleSidebarContext);
    const retailerId = useContext(RetailerIdContext);
    const { surfside, analytics } = useContext(ConfigContext);
    const [globalChecked] = useSharedOrderType();

    const sidebarRef = useRef(null);
    const closeButtonRef = useRef(null);
    const lastFocusedElementRef = useRef(null);

    const { data: cartData, isFetched: isFetchedCartData } =
        useCartData(retailerId);
    const { isFetched: isFetchedRetailerData } = useRetailerData(retailerId);
    const { data: specialsList, isFetched: isFetchedSpecials } =
        useSpecialsList(retailerId);
    const { data: menuData, isFetched: isFetchedMenuData } = useFetchRest(
        `data/menu?retailerId=${retailerId}`,
    );

    const [cartSpecials, setCartSpecials] = useState({});
    const [isValidAddress, setIsValidAddress] = useState(false);
    const hasTrackedCheckoutClick = useRef(false);
    const [cartEverOpened, setCartEverOpened] = useState(false);

    useEffect(() => {
        if (
            isFetchedCartData &&
            isFetchedSpecials &&
            isFetchedMenuData &&
            cartData?.items
        ) {
            const specials = processCartSpecials(
                cartData.items,
                specialsList,
                menuData,
            );
            setCartSpecials(specials);
        }
    }, [
        isFetchedCartData,
        isFetchedSpecials,
        isFetchedMenuData,
        cartData,
        specialsList,
        menuData,
    ]);

    const origin = window.location.origin;
    const checkoutUrl = cartData?.redirectUrl
        ? `${cartData.redirectUrl}?r=${origin}/order-confirmation/`
        : null;

    async function handleCheckoutClick() {
        if (globalChecked && globalChecked == 'DELIVERY' && !isValidAddress) {
            alert(
                'Please enter a valid delivery address before proceeding to checkout.',
            );
            return;
        }

        if (checkoutUrl === undefined || checkoutUrl === '') {
            if (
                confirm(
                    'Unfortunately there has been an issue with your checkout, please click OK to reload this page and try again.',
                )
            ) {
                location.reload();
            }
        } else {
            // Track Surfside event.
            if (surfside && surfside.enable && cartData && !hasTrackedCheckoutClick.current) {
                const surfsideDataWithSpecials = {
                    ...cartData,
                    items: cartData.items.map((item) => ({
                        ...item,
                        special: cartSpecials[item.productId] || null,
                    })),
                };
                didSurfsideLoad(() => {
                    trackSurfsideProductsLoop(
                        surfsideDataWithSpecials,
                        'checkout'
                    );
                });
            }

            // Handle GTM event
            if (analytics && analytics.gtm_id && cartData && !hasTrackedCheckoutClick.current) {
                const cartDataWithSpecials = {
                    ...cartData,
                    items: cartData.items.map((item) => ({
                        ...item,
                        special: cartSpecials[item.productId] || null,
                    })),
                };

                try {
                    hasTrackedCheckoutClick.current = true;
                    await gtmTrackEvent(
                        'begin_checkout',
                        cartDataWithSpecials,
                        null,
                        null,
                        null,
                        checkoutUrl,
                        analytics.isTerpli
                    );
                } catch (error) {
                    console.error('Error sending GTM event:', error);
                }

                // Failsafe redirect
                if (analytics.isTerpli) {
                    setTimeout(() => {
                        window.location.href = checkoutUrl;
                    }, 3000);
                } else {
                    window.location.href = checkoutUrl;
                }
            } else {
                // No tracking
                window.location.href = checkoutUrl;
            }
        }
    }

    // Focus management when sidebar opens/closes
    useEffect(() => {
        console.log('cart closed');
        if (sidebarToggle) {
            // Store the currently focused element
            lastFocusedElementRef.current = document.activeElement;
            // Focus the close button when sidebar opens
            closeButtonRef.current?.focus();
            // Add class to prevent main content scrolling
            document.body.style.overflow = 'hidden';
            console.log('cart opened');
            // Mark that the cart has been opened at least once
            setCartEverOpened(true);
        } else {
            // Return focus to the last focused element when sidebar closes
            lastFocusedElementRef.current?.focus();
            // Restore scrolling
            document.body.style.overflow = 'auto';
        }
    }, [sidebarToggle]);

    // Focus trap handling
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!sidebarToggle || !sidebarRef.current) return;

            if (e.key === 'Escape') {
                e.preventDefault();
                setSidebarToggle(false);
                return;
            }

            if (e.key === 'Tab') {
                const focusableElements = sidebarRef.current.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (e.shiftKey && document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement.focus();
                } else if (!e.shiftKey && document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement.focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [sidebarToggle, setSidebarToggle]);

    return (
        <div
            id="cart-sidebar"
            ref={sidebarRef}
            role="dialog"
            aria-modal="true"
            aria-label="Shopping Cart"
            className={`headless-cart-sidebar position-fixed d-flex flex-column bg-white ${sidebarToggle ? 'headless-show-sidebar' : ''
                }`}
            tabIndex="-1"
        >
            <div className="headless-cart-sidebar-header">
                <div className="headless-cart-sidebar-items" id="cart-items">
                    <div className="headless-subtitle headless-cart-sidebar-title">
                        Shopping cart
                    </div>
                </div>
                <button
                    ref={closeButtonRef}
                    className="headless-cart-sidebar-close closeCart"
                    onClick={() => setSidebarToggle(false)}
                    aria-label="Close cart"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM175 175c9.4-9.4 24.6-9.4 33.9 0l47 47 47-47c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-47 47 47 47c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-47-47-47 47c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l47-47-47-47c-9.4-9.4-9.4-24.6 0-33.9z" />
                    </svg>
                </button>
            </div>

            {surfside && surfside.enable && surfside.showCartBanner && cartEverOpened ? (
                <Suspense fallback={<LoadingSpinner />}>
                    <Surfside
                        placement='cart'
                        accountId={surfside.account_id}
                        sourceId={surfside.source_id}
                        placementId={surfside.placement_ids.cart}
                    />
                </Suspense>
            ) : null}

            {isFetchedRetailerData && isFetchedCartData ? (
                <OrderType forcedOrderType={forcedOrderType} />
            ) : null}

            {isFetchedCartData && cartData?.orderType === 'DELIVERY' && (
                <div className="headless-cart-sidebar-address">
                    <AddressInput setIsValidAddress={setIsValidAddress} />
                </div>
            )}

            <div className="headless-cart-sidebar-contents" id="cart-data">
                <CartProducts
                    surfside={surfside}
                    analytics={analytics}
                    cartIsOpen={sidebarToggle}
                    cartSidebar={true}
                />
            </div>
            <div className="headless-cart-sidebar-bottom">
                <div className="headless-cart-bottom-buttons">
                    <button
                        className="headless-cart-sidebar-continue"
                        onClick={() => setSidebarToggle(false)}
                    >
                        Continue Shopping
                    </button>
                    <div className="headless-cart-sidebar-totals" id="cart-totals">
                        Subtotal:{' '}
                        <span className="cart-sidebar-total-price">
                            {formattedPrice(
                                cartData?.priceSummary?.subtotal -
                                cartData?.priceSummary?.discounts,
                                true,
                            )}
                        </span>
                    </div>
                </div>
                <div className="headless-cart-sidebar-checkout" id="cart-checkout">
                    <button
                        id="cart-checkout-btn"
                        className="headless-button headless-cart-button"
                        onClick={handleCheckoutClick}
                    >
                        <span>Go to Checkout</span>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                            <path d="M505 273c9.4-9.4 9.4-24.6 0-33.9L369 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l95 95L24 232c-13.3 0-24 10.7-24 24s10.7 24 24 24l406.1 0-95 95c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0L505 273z" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CartSidebar;
